'use client';

import { createContext } from 'react';

import type { ScrollToIndex } from './CarouselInterface';
import type { Dispatch, SetStateAction } from 'react';

export type TCarouselContext = {
  /**
   * Indicates if the carousel is currently enabled.
   */
  enabled: boolean;

  /**
   * The indexes of the items that are currently fully visible.
   */
  visibleIndexes: number[];

  /**
   * The number of currently fully visible items.
   */
  visibleItems: number;

  /**
   * The index of the last item.
   */
  lastIndex: number;

  /**
   * The number of pixels the stage is scrolled.
   */
  stageScrollLeft: number;

  /**
   * The amount of pixels the stage would require to be rendered without a scrollbar -- its "width".
   */
  stageScrollWidth: number;

  /**
   * The current width of the stage -- its "width" as visible to the user.
   */
  stageWidth: number;

  scrollToIndex: ScrollToIndex;

  /**
   * A setter for the context.
   */
  set: Dispatch<SetStateAction<TCarouselContext>>;
};

/**
 * A context that provides information about the current state of the carousel, a helper function to scroll to a specific index and a setter for the context itself.
 */
export const CarouselContext = createContext<TCarouselContext>({
  enabled: false,
  visibleIndexes: [0],
  visibleItems: 0,
  lastIndex: 0,
  scrollToIndex: () => {},
  stageScrollLeft: 0,
  stageScrollWidth: 0,
  stageWidth: 0,
  set: () => {},
});
